import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import DayWindowField from './DayWindowField';
import type { MissionTerms, TermsRefusals } from './missionTerms';
import TariffField from './TariffField';
import TimeWindowPicker from './TimeWindowPicker';

interface MissionTermsCardProps {
  terms: MissionTerms;
  onChange: (patch: Partial<MissionTerms>) => void;
  /** A technician accepted (E14): window and hours locked, tariff and useful info still editable. */
  locked: boolean;
  /** Nothing left to send and nothing this desk can save: read-only. */
  disabled: boolean;
  refusals: TermsRefusals;
  askDeadline: boolean;
  /** The ⓘ beside the title, when the desk writes the deadline to Jira. */
  hint?: ReactNode;
  /** The desk's own sentences (Jira off, step passed). */
  notes?: ReactNode;
  footer: ReactNode;
}

/** « Mission · visible par le technicien » (F14, F15): window, hours, tariff and useful info. */
const MissionTermsCard = ({
  terms,
  onChange,
  locked,
  disabled,
  refusals,
  askDeadline,
  hint,
  notes,
  footer,
}: MissionTermsCardProps) => {
  const { t } = useTranslation();
  return (
    <fieldset
      aria-labelledby="assign-mission-terms"
      disabled={disabled}
      className="flex min-w-0 flex-col gap-4 rounded-md border border-zinc-200 p-4 dark:border-zinc-800"
    >
      <div className="flex items-center gap-1.5">
        {hint}
        <h3 id="assign-mission-terms" className="text-[13px] font-semibold">
          {t('Mission · visible to the technician')}
        </h3>
      </div>
      <DayWindowField
        earliest={terms.earliest}
        deadline={terms.deadline}
        onChange={onChange}
        locked={locked}
        refusal={locked ? null : refusals.day}
        startRefused={!locked && refusals.start}
        orderRefused={!locked && refusals.order}
        askDeadline={askDeadline}
      />
      {notes}
      <TimeWindowPicker
        value={terms.timeWindow}
        indicativeTime={terms.indicativeTime}
        disabled={locked}
        onChange={(timeWindow) => onChange({ timeWindow })}
        onIndicativeTimeChange={(indicativeTime) => onChange({ indicativeTime })}
      />
      <TariffField
        text={terms.tariffText}
        taxBasis={terms.taxBasis}
        onTextChange={(tariffText) => onChange({ tariffText })}
        onTaxBasisChange={(taxBasis) => onChange({ taxBasis })}
        refused={refusals.tariff}
        accepted={locked}
      />
      <div className="flex flex-col gap-1.5">
        <label htmlFor="assign-useful-info" className="text-[13px] font-medium">
          {t('Useful information')}
        </label>
        {/* Native: `components/ui/textarea` is untyped JSX. Same classes. */}
        <textarea
          id="assign-useful-info"
          rows={3}
          maxLength={1000}
          className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-describedby="assign-useful-info-hint"
          value={terms.usefulInfo}
          onChange={(event) => onChange({ usefulInfo: event.target.value })}
        />
        <p id="assign-useful-info-hint" className="text-[12px] text-muted-foreground">
          {t('Visible to the technician on their request.')}
        </p>
      </div>
      {footer}
    </fieldset>
  );
};

export default MissionTermsCard;
